/**
 * Soft-Gate-Banner (Juni 2026, Paket A): Erinnert unbestätigte Nutzer an die
 * E-Mail-Verifizierung. Kein Blocker — nur der Rechnungs-E-Mail-Versand ist
 * serverseitig gesperrt, bis die Adresse bestätigt wurde.
 */
import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { toast } from 'sonner';
import { MailWarning, X } from 'lucide-react';

const DISMISS_KEY = 'ivoliq_verify_banner_dismissed_session';

export default function EmailVerifyBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const [sending, setSending] = useState(false);

  if (!user || user.email_verified !== false || dismissed) return null;

  const resend = async () => {
    setSending(true);
    try {
      await api.post('/auth/resend-verification', null, { params: { email: user.email } });
      toast.success('Bestätigungslink erneut gesendet — bitte prüfe dein Postfach (auch Spam).');
    } catch {
      toast.error('Senden fehlgeschlagen. Bitte versuche es später erneut.');
    } finally {
      setSending(false);
    }
  };

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div
      className="flex items-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900"
      data-testid="email-verify-banner"
    >
      <MailWarning className="h-4 w-4 flex-shrink-0 text-amber-600" />
      <span className="min-w-0 flex-1 truncate">
        Bitte bestätige deine E-Mail-Adresse <strong>{user.email}</strong> — bis dahin ist der Rechnungsversand per E-Mail deaktiviert.
      </span>
      <button
        onClick={resend}
        disabled={sending}
        className="whitespace-nowrap rounded-md border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100 disabled:opacity-50"
        data-testid="email-verify-resend"
      >
        {sending ? 'Sendet…' : 'Link erneut senden'}
      </button>
      <button
        onClick={dismiss}
        className="text-amber-500 transition-colors hover:text-amber-700"
        aria-label="Hinweis ausblenden"
        data-testid="email-verify-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
